// route-info.service.ts
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { filter, map } from 'rxjs/operators';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';

export interface IRouteInfo {
  url: string;
  path: string;
  params: any;
  queryParams: any;
  fragment: string | null;
  data: any;
  timestamp: string;
}

@Injectable({
  providedIn: 'root',
})
export class RouteInfoService {
  constructor(private router: Router, private activatedRoute: ActivatedRoute) {}

  /**
   * Obtiene la URL actual completa
   * @returns string - URL completa actual
   */
  getCurrentUrl(): string {
    return this.router.url;
  }

  /**
   * Obtiene solo el path de la URL actual (sin query params ni fragments)
   * @returns string - Path de la URL actual
   */
  getCurrentPath(): string {
    return this.router.url.split('?')[0].split('#')[0];
  }

  /**
   * Obtiene los parámetros de la ruta actual
   * @returns any - Objeto con los parámetros de la ruta
   */
  getCurrentRouteParams(): any {
    return this.activatedRoute.snapshot.params;
  }

  /**
   * Obtiene los query parameters de la URL actual
   * @returns any - Objeto con los query parameters
   */
  getCurrentQueryParams(): any {
    return this.activatedRoute.snapshot.queryParams;
  }

  /**
   * Obtiene el fragment de la URL actual
   * @returns string | null - Fragment de la URL
   */
  getCurrentFragment(): string | null {
    return this.activatedRoute.snapshot.fragment;
  }

  /**
   * Obtiene datos asociados a la ruta actual
   * @returns any - Datos de la ruta
   */
  getCurrentRouteData(): any {
    return this.activatedRoute.snapshot.data;
  }

  /**
   * Observable que emite cada vez que cambia la ruta
   * @returns Observable<string> - Observable con la nueva URL
   */
  getRouteChanges(): Observable<string> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects)
    );
  }

  /**
   * Imprime en consola toda la información de la ruta actual
   */
  printCurrentRouteInfo(): void {
    console.groupEnd();
  }

  /**
   * Obtiene información detallada de la jerarquía de rutas
   * @returns any - Información completa de la jerarquía de rutas
   */
  getRouteHierarchy(): any {
    let route = this.activatedRoute;
    const hierarchy: any[] = [];

    while (route) {
      hierarchy.push({
        path: route.snapshot.routeConfig?.path || '',
        params: route.snapshot.params,
        queryParams: route.snapshot.queryParams,
        data: route.snapshot.data,
        url: route.snapshot.url.map((segment) => segment.path).join('/'),
      });
      route = route.firstChild as ActivatedRoute;
    }

    return hierarchy;
  }

  /**
   * Verifica si el usuario está en una ruta específica
   * @param route - Ruta a verificar
   * @returns boolean - true si está en la ruta especificada
   */
  isCurrentRoute(route: string): boolean {
    return this.getCurrentPath() === route;
  }

  /**
   * Verifica si el usuario está en una ruta que contiene un patrón específico
   * @param pattern - Patrón a buscar en la ruta
   * @returns boolean - true si la ruta contiene el patrón
   */
  routeContains(pattern: string): boolean {
    return this.getCurrentPath().includes(pattern);
  }
}
